
import React, { useState, useContext } from 'react';
import { AppContext } from '../App';
import { Client, BriefingField } from '../types';
import { supabase } from '../supabase';

const Onboarding: React.FC = () => {
  const context = useContext(AppContext);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [finished, setFinished] = useState(false);

  const [clientData, setClientData] = useState({
    name: '',
    cnpj: '',
    email: '',
    phone: '',
    contactName: '',
    address: '',
    type: 'SaaS',
    logoUrl: '',
    tradingName: '',
    website: ''
  });

  const [docType, setDocType] = useState<'CNPJ' | 'CPF'>('CNPJ');

  const [briefingResponses, setBriefingResponses] = useState<Record<string, string>>({});

  if (!context) return null;
  const { company, syncAllData } = context;

  React.useEffect(() => {
    syncAllData();
  }, []);

  const briefingTemplate = company.briefingTemplate || [];
  const totalSteps = 2 + briefingTemplate.length;

  const formatDoc = (value: string, type: 'CNPJ' | 'CPF') => {
    const digits = value.replace(/\D/g, '');
    if (type === 'CPF') {
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .substring(0, 14);
    }
    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .substring(0, 18);
  };

  const handleNext = () => setStep(prev => prev + 1);
  const handleBack = () => setStep(prev => prev - 1);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const formattedBriefing: BriefingField[] = briefingTemplate.map(field => ({
      ...field,
      value: briefingResponses[field.id] || ''
    }));

    const newClientId = `#CL-${Math.floor(Math.random() * 9000) + 1000}`;

    const dbData = {
      id: newClientId,
      name: clientData.name,
      type: clientData.type,
      cnpj: clientData.cnpj,
      email: clientData.email,
      phone: clientData.phone,
      address: clientData.address,
      website: clientData.website,
      trading_name: clientData.tradingName,
      contact_name: clientData.contactName,
      contact_role: 'Proprietário',
      logo_url: clientData.logoUrl || `https://ui-avatars.com/api/?name=${clientData.name}&background=8a0049&color=fff&rounded=true`,
      avatar_url: `https://ui-avatars.com/api/?name=${clientData.contactName}&background=fcebf5&color=8a0049&rounded=true`,
      status: 'Active',
      active_projects_count: 0,
      briefing: formattedBriefing
    };

    try {
      const { error } = await supabase.from('clients').insert([dbData]);
      if (error) throw error;

      await syncAllData();
      setFinished(true);
    } catch (err: any) {
      alert('Erro ao enviar cadastro: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = (step / totalSteps) * 100;

  if (finished) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center p-6 text-center font-display">
        <div className="max-w-xl w-full animate-in zoom-in duration-700">
          <div className="bg-white p-12 md:p-20 rounded-[23px] shadow-2xl border border-gray-100">
            <div className="size-24 bg-green-50 text-green-500 rounded-[23px] flex items-center justify-center mx-auto mb-10 shadow-lg shadow-green-500/10">
              <span className="material-symbols-outlined text-5xl">verified</span>
            </div>
            <h1 className="text-4xl font-black text-text-main mb-6 tracking-tight">Seja Bem-vindo!</h1>
            <p className="text-lg text-text-secondary font-bold leading-relaxed mb-10">
              Recebemos seu cadastro e briefing com sucesso. Nossa equipe da <strong>{company.name}</strong> já está analisando suas informações.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light flex flex-col items-center p-6 py-12 md:py-20 font-display">
      <div className="mb-12 flex flex-col items-center text-center animate-in fade-in duration-1000">
        <div className="mb-6 flex items-center justify-center">
          {company.logoUrl ? (
            <img src={company.logoUrl} className="h-24 w-auto object-contain" alt="Logo" />
          ) : (
            <span className="material-symbols-outlined text-6xl text-primary">hexagon</span>
          )}
        </div>
        <h2 className="text-[10px] font-black text-text-secondary uppercase tracking-[0.5em] opacity-40">Portal de Onboarding • {company.name}</h2>
      </div>

      <div className="max-w-3xl w-full">
        <div className="w-full bg-gray-100 h-2.5 rounded-full mb-10 overflow-hidden shadow-inner relative">
          <div
            className="bg-primary h-full transition-all duration-1000 ease-out rounded-full shadow-[0_0_15px_rgba(138,0,73,0.3)]"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="bg-white p-10 md:p-16 rounded-[23px] shadow-2xl border border-gray-100 animate-in slide-in-from-bottom-8 duration-700 relative overflow-hidden">

          {step === 1 && (
            <div className="space-y-10 animate-in fade-in duration-500">
              <div className="space-y-4">
                <span className="text-primary font-black text-[10px] uppercase tracking-widest bg-primary-light px-4 py-1.5 rounded-full">Passo 01/02</span>
                <h1 className="text-3xl md:text-4xl font-black text-text-main tracking-tight leading-tight">Olá! Conte-nos um pouco sobre sua empresa.</h1>
              </div>
              <div className="grid grid-cols-1 gap-6">
                <div className="flex flex-col gap-2">
                  <label className="text-[11px] font-black uppercase text-text-secondary tracking-widest ml-1">Razão Social ou Nome</label>
                  <input
                    className="h-16 px-8 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-lg transition-all shadow-sm"
                    value={clientData.name}
                    onChange={e => setClientData({ ...clientData, name: e.target.value })}
                    placeholder="Nome Fantasia"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[11px] font-black uppercase text-text-secondary tracking-widest ml-1">Razão Social (Opcional)</label>
                  <input
                    className="h-16 px-8 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-lg transition-all shadow-sm"
                    value={clientData.tradingName}
                    onChange={e => setClientData({ ...clientData, tradingName: e.target.value })}
                    placeholder="Razão Social Ltda"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center ml-1">
                    <label className="text-[11px] font-black uppercase text-text-secondary tracking-widest">{docType}</label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setDocType('CNPJ')} className={`text-[10px] font-black px-4 py-1.5 rounded-full transition-all ${docType === 'CNPJ' ? 'bg-primary text-white' : 'bg-gray-100 text-text-secondary'}`}>CNPJ</button>
                      <button type="button" onClick={() => setDocType('CPF')} className={`text-[10px] font-black px-4 py-1.5 rounded-full transition-all ${docType === 'CPF' ? 'bg-primary text-white' : 'bg-gray-100 text-text-secondary'}`}>CPF</button>
                    </div>
                  </div>
                  <input
                    className="h-16 px-8 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-lg transition-all shadow-sm"
                    value={clientData.cnpj}
                    maxLength={docType === 'CNPJ' ? 18 : 14}
                    onChange={e => setClientData({ ...clientData, cnpj: formatDoc(e.target.value, docType) })}
                    placeholder={docType === 'CNPJ' ? "00.000.000/0001-00" : "000.000.000-00"}
                  />
                </div>
              </div>
              <button
                onClick={handleNext}
                disabled={!clientData.name || (docType === 'CNPJ' ? clientData.cnpj.length < 18 : clientData.cnpj.length < 14)}
                className="w-full h-16 bg-primary text-white rounded-[30px] font-black uppercase tracking-widest shadow-xl shadow-primary/30 hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-30"
              >
                Continuar
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-10 animate-in fade-in duration-500">
              <div className="space-y-4">
                <span className="text-primary font-black text-[10px] uppercase tracking-widest bg-primary-light px-4 py-1.5 rounded-full">Passo 02/02</span>
                <h1 className="text-3xl md:text-4xl font-black text-text-main tracking-tight leading-tight">Como podemos falar com você?</h1>
              </div>
              <div className="space-y-6">
                <div className="flex flex-col gap-2">
                  <label className="text-[11px] font-black uppercase text-text-secondary tracking-widest ml-1">Nome do Contato Principal</label>
                  <input
                    className="h-16 px-8 rounded-2xl bg-background-light border-2 border-transparent focus:border-primary/20 focus:bg-white outline-none font-bold text-lg transition-all shadow-sm"
                    value={clientData.contactName}
                    onChange={e => setClientData({ ...clientData, contactName: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label className="text-[11px] font-black uppercase text-text-secondary tracking-widest ml-1">E-mail</label>
                    <input
                      type="email"
                      className="h-16 px-8 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-lg transition-all shadow-sm"
                      value={clientData.email}
                      onChange={e => setClientData({ ...clientData, email: e.target.value })}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[11px] font-black uppercase text-text-secondary tracking-widest ml-1">Telefone / WhatsApp</label>
                    <input
                      className="h-16 px-8 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-lg transition-all shadow-sm"
                      value={clientData.phone}
                      onChange={e => setClientData({ ...clientData, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label className="text-[11px] font-black uppercase text-text-secondary tracking-widest ml-1">Site / Portfólio (Opcional)</label>
                    <input
                      className="h-16 px-8 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-lg transition-all shadow-sm"
                      value={clientData.website}
                      onChange={e => setClientData({ ...clientData, website: e.target.value })}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[11px] font-black uppercase text-text-secondary tracking-widest ml-1">Endereço (Opcional)</label>
                    <input
                      className="h-16 px-8 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-lg transition-all shadow-sm"
                      value={clientData.address}
                      onChange={e => setClientData({ ...clientData, address: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-4 pt-4">
                <button onClick={handleBack} className="h-16 px-8 rounded-[30px] border-2 border-gray-100 font-black text-text-secondary uppercase text-[10px] tracking-widest hover:bg-gray-50 transition-all">Voltar</button>
                <button
                  onClick={briefingTemplate.length > 0 ? handleNext : handleSubmit}
                  disabled={!clientData.contactName || !clientData.email}
                  className="flex-1 h-16 bg-primary text-white rounded-[30px] font-black uppercase tracking-widest shadow-xl shadow-primary/30 hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-30"
                >
                  {briefingTemplate.length > 0 ? 'Prosseguir para o Quiz' : 'Finalizar Cadastro'}
                </button>
              </div>
            </div>
          )}

          {step > 2 && step <= totalSteps && (
            <div className="space-y-10 animate-in slide-in-from-right-8 duration-500">
              {(() => {
                const fieldIdx = step - 3;
                const field = briefingTemplate[fieldIdx];
                return (
                  <>
                    <div className="space-y-4">
                      <span className="text-primary font-black text-[10px] uppercase tracking-widest bg-primary-light px-4 py-1.5 rounded-full">Pergunta {fieldIdx + 1}/{briefingTemplate.length}</span>
                      <h1 className="text-3xl md:text-4xl font-black text-text-main tracking-tight leading-tight">{field.label}</h1>
                    </div>

                    <div className="min-h-[160px]">
                      {field.type === 'textarea' && (
                        <textarea
                          rows={4}
                          className="w-full p-8 rounded-[23px] bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-lg transition-all resize-none shadow-sm"
                          value={briefingResponses[field.id] || ''}
                          onChange={e => setBriefingResponses({ ...briefingResponses, [field.id]: e.target.value })}
                          placeholder={field.placeholder}
                          autoFocus
                        />
                      )}

                      {['text', 'email', 'tel', 'url', 'number', 'date'].includes(field.type) && (
                        <input
                          type={field.type === 'text' ? 'text' : field.type}
                          className="w-full h-16 px-8 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-lg transition-all shadow-sm"
                          value={briefingResponses[field.id] || ''}
                          onChange={e => setBriefingResponses({ ...briefingResponses, [field.id]: e.target.value })}
                          placeholder={field.placeholder}
                          autoFocus
                        />
                      )}

                      {field.type === 'select' && (
                        <div className="grid grid-cols-1 gap-3">
                          {field.options?.map(opt => (
                            <button
                              key={opt}
                              onClick={() => setBriefingResponses({ ...briefingResponses, [field.id]: opt })}
                              className={`h-14 px-8 rounded-[30px] border-2 text-left font-black text-sm transition-all flex items-center justify-between ${briefingResponses[field.id] === opt
                                ? 'border-primary bg-primary/5 text-primary shadow-md'
                                : 'border-gray-50 bg-gray-50 text-text-secondary hover:border-gray-200'
                                }`}
                            >
                              <span>{opt}</span>
                              {briefingResponses[field.id] === opt && <span className="material-symbols-outlined text-[20px]">check_circle</span>}
                            </button>
                          ))}
                        </div>
                      )}

                      {field.type === 'checkbox' && (
                        <div className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 border-2 border-gray-200 hover:bg-white hover:border-primary/20 transition-all cursor-pointer" onClick={() => setBriefingResponses({ ...briefingResponses, [field.id]: briefingResponses[field.id] === 'Sim' ? 'Não' : 'Sim' })}>
                          <div className={`size-8 rounded-lg border-2 flex items-center justify-center transition-all ${briefingResponses[field.id] === 'Sim' ? 'bg-primary border-primary text-white' : 'border-gray-300 bg-white'}`}>
                            {briefingResponses[field.id] === 'Sim' && <span className="material-symbols-outlined text-lg">check</span>}
                          </div>
                          <span className="font-bold text-lg text-text-main">{field.placeholder || 'Sim, confirmo esta opção'}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-4">
                      <button onClick={handleBack} className="h-16 px-8 rounded-[30px] border-2 border-gray-100 font-black text-text-secondary uppercase text-[10px] tracking-widest hover:bg-gray-50 transition-all">Anterior</button>
                      <button
                        onClick={step === totalSteps ? handleSubmit : handleNext}
                        disabled={!briefingResponses[field.id] || isSubmitting}
                        className="flex-1 h-16 bg-primary text-white rounded-[30px] font-black uppercase tracking-widest shadow-xl shadow-primary/30 hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-30"
                      >
                        {isSubmitting ? 'Enviando...' : step === totalSteps ? 'Finalizar Tudo' : 'Continuar'}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
        <p className="text-center text-[10px] font-black text-text-secondary uppercase tracking-[0.4em] mt-12 opacity-20">Sistema Magenta • 2026</p>
      </div>
    </div>
  );
};

export default Onboarding;
